//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  CUSTOM_DATE_RANGE_DISPLAY_NAME,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { isDefined } from '@utils/filterPredicates';
import { momentFromDate } from '@utils/moment';

export const monthDayYearDateFormat = 'MMM DD, YYYY';

export const timeMonthDayYearFormat = 'h:mm a [on] MMM DD, YYYY';

export const monthDayYearTimeTimezoneFormat = 'MMM DD, YYYY [at] h:mm A z';

export const parseDateRangeStringFromFilter = (dateRange: IUnisonDateRange) => {
  const alias = dateRange.alias;

  switch (alias) {
    case 'custom':
      return buildCustomDateRangeString(
        dateRange.startDate,
        dateRange.endDate,
        '/',
      );
    case 'last24Hours':
    case 'last7Days':
    case 'last30Days':
    case 'last3Months':
      return alias;
    default:
      return '';
  }
};

export const buildCustomDateRangeString = (
  startDate: Date,
  endDate: Date,
  separator: string,
  format: string = 'YYYY-MM-D',
) => {
  const from = moment(startDate).format(format);
  const to = moment(endDate).format(format);

  return `${from}${separator}${to}`;
};

/**
 * Create an english expression of the date range.
 * e.g. "[No stories found] in the last 7 days"
 * e.g. "[No stories found] between 3/12/12 and 3/45/45"
 */
export const dateRangeExpression = (dateRange: IUnisonDateRange) => {
  switch (dateRange.alias) {
    case 'allTime':
      return '';
    case 'last3Months':
      return ' in the last 3 months';
    case 'today':
      return ' today';
    case 'custom': {
      // Unison Date Ranges are always in browser timezone.
      const startDate = moment(dateRange.startDate).format('ll');
      const endDate = moment(dateRange.endDate).format('ll');
      if (startDate === endDate) {
        return ` on ${startDate}`;
      } else {
        return ` between ${startDate} and ${endDate}`;
      }
    }
    default: {
      const res = /last(\d+)Days/.exec(dateRange.alias);
      if (res) {
        const [, dayCount] = res;
        return ` in the last ${dayCount} days`;
      }
      // Unknown alias, please add an explicit case!
      return '';
    }
  }
};

/**
 * Checks if the given `date` is within the current week,
 * according to the user's time zone
 */
export const isCurrentWeek = (date: Date, timeZone: TimeZoneId): boolean =>
  DateString.getDayOffset(
    DateString.getToday(timeZone),
    DateString.fromTimestamp(date, timeZone),
  ) < 7;

/**
 * @param {string} isoDate - The date to format
 * @param {TimeZoneId} userTimeZone - The timezone Id
 * @param {string} format - The format to use to eg: 'h:mm a [on] MMM DD, YYYY'
 *
 */
export const formatDateWithTime = (
  isoDate: string | number,
  userTimeZone: TimeZoneId,
  format: string,
) => {
  const dateTime = moment.tz(isoDate, userTimeZone);
  return dateTime.format(format);
};

/**
 * Formats a given `date` string in the format of
 *  "Month Day, Year" (e.g., "January 1, 2020").
 *
 * @param {string} date - The date to format.
 * @returns {string} The formatted date string.
 */
export const formatDate = (date: string): string => {
  const formattedDate = new Date(date).toLocaleString('default', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return formattedDate;
};

/**
 * Date regex to match a valid date in the format 'MM/DD/YYYY'
 */
export const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * Given a string, returns a UTC date if the string is a valid date in the format 'MM/DD/YYYY'
 *
 * @param dateString A string to parse as a date
 * @returns A date if the string is a valid date, otherwise undefined
 * @example parseDate('12/31/2021') // returns a Date object
 * @example parseDate('12/31/21') // returns undefined
 * @example parseDate('12/31/2021 12:00:00') // returns undefined
 */
export const parseOptionalUTCDate = (
  dateString?: string,
  defaultValue?: Date,
): Date | undefined => {
  if (dateString && dateRegex.test(dateString)) {
    return moment.utc(dateString, 'MM/DD/YYYY').toDate();
  }
  return defaultValue ? moment(defaultValue).utc().toDate() : undefined;
};

/**
 * Converts a date range with timeZone into timestamps
 *
 * @param dateRange - The aliased date range containing startDate, endDate, and timeZone.
 * @param includeEndOfDay - If true, adjusts the endDate to the end of that day (23:59:59.999).
 * @returns startTimestamp, endTimestamp
 */
export const getRangeTimestampsFromAliasedDateRange = (
  dateRange: IAliasedDateRange,
  includeEndOfDay = false,
): {
  startTimestamp: number;
  endTimestamp: number;
} => {
  const { endDate, startDate, timeZone } = dateRange;

  const startTimestamp = moment.tz(startDate, timeZone).valueOf();
  const endMoment = moment.tz(endDate, timeZone);
  const endTimestamp = (
    includeEndOfDay ? endMoment.endOf('day') : endMoment
  ).valueOf();

  return {
    startTimestamp,
    endTimestamp,
  };
};

export const getDateRangeBoundsInMs = (
  dateRange: IAliasedDateRange | undefined,
) => {
  const { endDate, startDate, timeZone } = dateRange ?? {};

  // Interpret the selected dates in the provided timezone.
  // Start is inclusive at start-of-day; End is inclusive at end-of-day.
  const from =
    isDefined(startDate) && isDefined(timeZone)
      ? DateString.toMoment(startDate, timeZone).startOf('day').valueOf()
      : undefined;
  const to =
    isDefined(endDate) && isDefined(timeZone)
      ? DateString.toMoment(endDate, timeZone).endOf('day').valueOf()
      : undefined;

  return { from, to };
};

/**
 * Given a date range object with DateString start and end dates,
 * returns a new date range representing the comparison period.
 * The comparison period has the same duration as the original period,
 * but it is shifted backward by the duration plus one day.
 */
export const getComparisonDateRange = (
  dateRange: IAliasedDateRange,
): IAliasedDateRange => {
  // Calculate the number of days between the start and end date of the dateRange
  const span = DateString.getDayOffset(dateRange.startDate, dateRange.endDate);

  // The shift amount is the duration of the range plus one day.
  const shift = span + 1;

  return {
    ...dateRange,
    displayName: CUSTOM_DATE_RANGE_DISPLAY_NAME,
    startDate: DateString.addDays(dateRange.startDate, -shift),
    endDate: DateString.addDays(dateRange.endDate, -shift),
  };
};

/**
 * Given a date string, returns a Date object representing the start of that day in the specified timezone.
 */
export const getStartOfDay = (date: DateString, timeZone: TimeZoneId): Date =>
  DateString.toMoment(date, timeZone).startOf('day').toDate();

/**
 * Given a date string, returns a Date object representing the end of that day in the specified timezone.
 */
export const getEndOfDay = (date: DateString, timeZone: TimeZoneId): Date =>
  DateString.toMoment(date, timeZone).endOf('day').toDate();
